import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, ArrowDown } from "lucide-react";
import { MaskReveal } from "./Reveal";
import { COMPANY } from "./data";

const HERO_BG = "/images/hero-bg.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0, 0.4]);

  const scrollTo = (sel) =>
    document.querySelector(sel)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_BG}
          alt="Security professionals standing guard beside a Pacific Northwest totem pole"
          className="w-full h-full object-cover brightness-[1.28] contrast-105"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-black"
      />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[#050505] via-[#050505]/25 to-black/20" />
      <div className="absolute inset-0 z-[2] bg-gradient-to-r from-[#050505]/85 via-[#050505]/20 to-transparent" />

      <div className="relative z-10 mx-auto max-w-7xl w-full px-5 md:px-8 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-5"
        >
          <span className="flex items-center gap-2 text-[color:var(--primary-text)] text-xs font-bold uppercase tracking-[0.24em]">
            <ShieldCheck size={16} /> 100% Indigenous-Owned
          </span>
          <span className="h-px w-14 bg-white/20" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7 }}
          className="mb-6"
          data-testid="hero-company-name"
        >
          <span className="block font-display font-semibold tracking-tight leading-[0.95] text-4xl md:text-6xl lg:text-7xl">
            Chowat Security
          </span>
          <span className="block font-display font-medium tracking-tight text-2xl md:text-4xl lg:text-5xl text-[color:var(--primary-text)]">
            Services Inc.
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.7 }}
          data-testid="hero-slogan"
          className="mb-8 -mt-1 flex items-center gap-4 text-secondary"
        >
          <span className="h-px w-10 bg-[color:var(--primary)]" />
          <span className="font-editorial italic text-lg md:text-2xl text-white/85">
            “{COMPANY.slogan}”
          </span>
        </motion.p>

        <h1 className="font-display font-semibold tracking-tighter leading-[0.95] text-3xl md:text-5xl max-w-4xl">
          <MaskReveal delay={0.15}>Protection built on</MaskReveal>
          <MaskReveal delay={0.28} className="text-[color:var(--primary-text)]">
            trust, training
          </MaskReveal>
          <MaskReveal delay={0.41}>
            <span>
              &amp; <span className="font-editorial italic font-500">cultural values</span>.
            </span>
          </MaskReveal>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-8 max-w-xl text-lg md:text-xl text-secondary leading-relaxed"
        >
          {COMPANY.name} delivers professional mobile patrol, static guarding and
          site security across the region — where safety is never negotiable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#contact")}
            data-testid="hero-quote-button"
            className="group inline-flex items-center gap-2 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-semibold px-7 py-4 rounded-sm transition-colors"
          >
            Request a Quote
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
          <button
            onClick={() => scrollTo("#services")}
            data-testid="hero-services-button"
            className="inline-flex items-center gap-2 border border-white/25 hover:border-[color:var(--primary)] hover:bg-[color:var(--primary)]/10 text-white font-semibold px-7 py-4 rounded-sm transition-colors"
          >
            Explore Services
          </button>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-secondary text-xs uppercase tracking-widest"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

